/* eslint-disable no-param-reassign */
import { DEFAULT_PLAYER_TIME } from '@constants/unacademyIcons/icons.constants';
import {
  PLAYER_CONTROLS,
  VIDEO_QUALITY,
  VIDEO_AUDIO,
  VIDEO_SPEED
} from 'types/unacademyIcons/player.types';
import create, { SetState } from 'zustand';
import createStoreSelectors from './utils/createStoreSelector';

type LessonPlayerStoreType = {
  isPlayerPaused: boolean;
  isFeedbackVisible: boolean;
  isCoverLoaderVisible: boolean;
  isFullscreen: boolean;
  lessonTitle: string;
  lessonDescription: string;
  lessonSlug: string;
  lessonUuid: string;
  courseSlug: string | string[];
  isNextComingSoon: boolean;
  nextSlug: string;
  nextUuid: string;
  nextCover: string;
  nextTitle: string | null;
  nextLessonPublishDate: string | null;
  isControlsVisible: boolean;
  isXtraVisible: boolean;
  isQuizVisible: boolean;
  isSkipVisible: boolean;
  player: any;
  lessonPlaylist: any;
  lessonSubtitles: any;
  duration: string;
  skipIntroStartAt: number;
  skipIntroEndAt: number;
  staticBreakdowns: any;
  quizBreakdowns: any;
  currentVideoQuality: VIDEO_QUALITY | string;
  currentVideoAudio: VIDEO_AUDIO | string;
  currentVideoSpeed: VIDEO_SPEED | string;
  currentSubtitle: string | null;
  feedbackType: PLAYER_CONTROLS | null;
  lessonOrder: number | null;
  isNextOverlayVisible: boolean;
  isNextLessonCancelled: boolean;
  isNextLessonVisible: boolean;
  lessonToken: string | null;
  lessonCover: string | null;
  setPlayer: (player: any) => void;
  setLessonPlaylist: (lessonPlaylist: any) => void;
  setLessonSubtitles: (lessonSubtitles: any) => void;
  setVideoSettings: ({
    currentVideoQuality,
    currentVideoAudio,
    currentVideoSpeed,
    currentSubtitle
  }: {
    currentVideoQuality: VIDEO_QUALITY | string;
    currentVideoAudio: VIDEO_AUDIO | string;
    currentVideoSpeed: VIDEO_SPEED | string;
    currentSubtitle: string | null;
  }) => void;
  setStaticBreakdowns: (staticBreakdowns: any) => void;
  setQuizBreakdowns: (quizBreakdowns: any) => void;
  setPaused: (isPlayerPaused: boolean) => void;
  setIsCoverLoaderVisible: (isCoverLoaderVisible: boolean) => void;
  setFeedback: (
    isFeedbackVisible: boolean,
    feedbackType: PLAYER_CONTROLS | null
  ) => void;
  setFullscreen: (isFullscreen: boolean) => void;
  setLessonDetails: (
    skipIntroStartAt: number,
    skipIntroEndAt: number,
    lessonTitle: string,
    lessonDescription: string,
    lessonSlug: string,
    lessonUuid: string,
    courseSlug: string | string[],
    lessonOrder: number,
    lessonToken: string,
    lessonCover: string
  ) => void;
  setNextLesson: (
    isNextComingSoon: boolean,
    nextSlug: string,
    nextUuid: string,
    nextCover: string,
    nextLessonPublishDate: string | null,
    nextTitle: string | null
  ) => void;
  setNextOverlayVisible: (isNextOverlayVisible: boolean) => void;
  setNextLessonVisible: (isNextLessonVisible: boolean) => void;
  setNextLessonCancelled: (isNextLessonCancelled: boolean) => void;
  setControlsIsVisible: (isControlsVisible: boolean) => void;
  setXtraIsVisible: (isXtraVisible: boolean) => void;
  setQuizIsVisible: (isQuizVisible: boolean) => void;
  setSkipIsVisible: (isSkipVisible: boolean) => void;
  resetLessonPlayer: () => void;
};

const useStore = create<LessonPlayerStoreType>(
  (set: SetState<LessonPlayerStoreType>) => ({
    isPlayerPaused: true,
    isFeedbackVisible: false,
    isCoverLoaderVisible: false,
    isFullscreen: false,
    lessonTitle: '',
    lessonDescription: '',
    lessonSlug: '',
    lessonUuid: '',
    courseSlug: '',
    isNextComingSoon: false,
    nextSlug: '',
    nextUuid: '',
    nextCover: '',
    nextTitle: '',
    nextLessonPublishDate: null,
    isControlsVisible: false,
    isXtraVisible: false,
    isQuizVisible: false,
    isSkipVisible: false,
    player: null,
    lessonPlaylist: null,
    lessonSubtitles: null,
    duration: DEFAULT_PLAYER_TIME,
    skipIntroStartAt: 0,
    skipIntroEndAt: 0,
    staticBreakdowns: null,
    quizBreakdowns: null,
    currentVideoQuality: VIDEO_QUALITY.AUTO,
    currentVideoAudio: VIDEO_AUDIO.ENGLISH,
    currentVideoSpeed: VIDEO_SPEED.NORMAL,
    currentSubtitle: 'Off',
    feedbackType: null,
    lessonOrder: 1,
    isNextOverlayVisible: false,
    isNextLessonCancelled: false,
    isNextLessonVisible: false,
    lessonToken: null,
    lessonCover: null,
    setPaused: (isPlayerPaused: boolean): void =>
      set((state) => {
        state.isPlayerPaused = isPlayerPaused;
      }),
    setIsCoverLoaderVisible: (isCoverLoaderVisible: boolean): void =>
      set((state) => {
        state.isCoverLoaderVisible = isCoverLoaderVisible;
      }),
    setFeedback: (
      isFeedbackVisible: boolean,
      feedbackType: PLAYER_CONTROLS | null
    ): void =>
      set((state) => {
        state.isFeedbackVisible = isFeedbackVisible;
        state.feedbackType = feedbackType;
      }),
    setFullscreen: (isFullscreen: boolean): void =>
      set((state) => {
        state.isFullscreen = isFullscreen;
      }),
    setControlsIsVisible: (isControlsVisible: boolean): void =>
      set((state) => {
        state.isControlsVisible = isControlsVisible;
      }),
    setXtraIsVisible: (isXtraVisible: boolean): void =>
      set((state) => {
        state.isXtraVisible = isXtraVisible;
      }),
    setQuizIsVisible: (isQuizVisible: boolean): void =>
      set((state) => {
        state.isQuizVisible = isQuizVisible;
      }),
    setSkipIsVisible: (isSkipVisible: boolean): void =>
      set((state) => {
        state.isSkipVisible = isSkipVisible;
      }),
    setPlayer: (player: any): void =>
      set((state) => {
        state.player = player;
      }),
    setLessonPlaylist: (lessonPlaylist: any) =>
      set((state) => {
        state.lessonPlaylist = lessonPlaylist;
      }),
    setLessonSubtitles: (lessonSubtitles: any) =>
      set((state) => {
        state.lessonSubtitles = lessonSubtitles;
      }),
    setVideoSettings: ({
      currentVideoQuality,
      currentVideoAudio,
      currentVideoSpeed,
      currentSubtitle
    }: {
      currentVideoQuality: VIDEO_QUALITY;
      currentVideoAudio: VIDEO_AUDIO | string;
      currentVideoSpeed: VIDEO_SPEED;
      currentSubtitle: string | null;
    }) =>
      set((state) => {
        state.currentVideoQuality = currentVideoQuality;
        state.currentVideoAudio = currentVideoAudio;
        state.currentVideoSpeed = currentVideoSpeed;
        state.currentSubtitle = currentSubtitle;
      }),
    setStaticBreakdowns: (staticBreakdowns: any): void =>
      set((state) => {
        state.staticBreakdowns = staticBreakdowns;
      }),
    setQuizBreakdowns: (quizBreakdowns: any): void =>
      set((state) => {
        state.quizBreakdowns = quizBreakdowns;
      }),
    setLessonDetails: (
      skipIntroStartAt: number,
      skipIntroEndAt: number,
      lessonTitle: string,
      lessonDescription: string,
      lessonSlug: string,
      lessonUuid: string,
      courseSlug: string | string[],
      lessonOrder: number,
      lessonToken: string,
      lessonCover: string
    ): void =>
      set((state) => {
        state.skipIntroStartAt = skipIntroStartAt;
        state.skipIntroEndAt = skipIntroEndAt;
        state.lessonTitle = lessonTitle;
        state.lessonDescription = lessonDescription;
        state.lessonSlug = lessonSlug;
        state.lessonUuid = lessonUuid;
        state.courseSlug = courseSlug;
        state.lessonOrder = lessonOrder;
        state.lessonToken = lessonToken;
        state.lessonCover = lessonCover;
      }),
    setNextLesson: (
      isNextComingSoon: boolean,
      nextSlug: string,
      nextUuid: string,
      nextCover: string,
      nextLessonPublishDate: string | null,
      nextTitle: string | null
    ): void =>
      set((state) => {
        state.isNextComingSoon = isNextComingSoon;
        state.nextSlug = nextSlug;
        state.nextUuid = nextUuid;
        state.nextCover = nextCover;
        state.nextLessonPublishDate = nextLessonPublishDate;
        state.nextTitle = nextTitle;
      }),
    setNextOverlayVisible: (isNextOverlayVisible: boolean): void =>
      set((state) => {
        state.isNextOverlayVisible = isNextOverlayVisible;
      }),
    setNextLessonVisible: (isNextLessonVisible: boolean): void =>
      set((state) => {
        state.isNextLessonVisible = isNextLessonVisible;
      }),
    setNextLessonCancelled: (isNextLessonCancelled: boolean): void =>
      set((state) => {
        state.isNextLessonCancelled = isNextLessonCancelled;
      }),
    resetLessonPlayer: (): void =>
      set((state) => {
        state.isPlayerPaused = true;
        state.isNextComingSoon = false;
        state.duration = DEFAULT_PLAYER_TIME;
        state.isControlsVisible = false;
        state.player = null;
        state.lessonPlaylist = null;
        state.lessonSubtitles = null;
        state.isXtraVisible = false;
        state.isQuizVisible = false;
        state.staticBreakdowns = null;
        state.quizBreakdowns = null;
        state.isNextLessonVisible = false;
        state.isNextLessonCancelled = false;
        state.isNextOverlayVisible = false;
        state.nextLessonPublishDate = null;
        state.nextTitle = '';
        state.isCoverLoaderVisible = false;
      })
  })
);

const useLessonPlayer = createStoreSelectors(useStore);

const useLessonPlayerStore = () => {
  return {
    isPlayerPaused: useLessonPlayer.use.isPlayerPaused(),
    isFeedbackVisible: useLessonPlayer.use.isFeedbackVisible(),
    isCoverLoaderVisible: useLessonPlayer.use.isCoverLoaderVisible(),
    isFullscreen: useLessonPlayer.use.isFullscreen(),
    lessonTitle: useLessonPlayer.use.lessonTitle(),
    lessonDescription: useLessonPlayer.use.lessonDescription(),
    lessonSlug: useLessonPlayer.use.lessonSlug(),
    lessonCover: useLessonPlayer.use.lessonCover(),
    lessonUuid: useLessonPlayer.use.lessonUuid(),
    courseSlug: useLessonPlayer.use.courseSlug(),
    isNextComingSoon: useLessonPlayer.use.isNextComingSoon(),
    nextSlug: useLessonPlayer.use.nextSlug(),
    nextUuid: useLessonPlayer.use.nextUuid(),
    nextCover: useLessonPlayer.use.nextCover(),
    nextTitle: useLessonPlayer.use.nextTitle(),
    nextLessonPublishDate: useLessonPlayer.use.nextLessonPublishDate(),
    isControlsVisible: useLessonPlayer.use.isControlsVisible(),
    isXtraVisible: useLessonPlayer.use.isXtraVisible(),
    isQuizVisible: useLessonPlayer.use.isQuizVisible(),
    isSkipVisible: useLessonPlayer.use.isSkipVisible(),
    player: useLessonPlayer.use.player(),
    lessonPlaylist: useLessonPlayer.use.lessonPlaylist(),
    lessonSubtitles: useLessonPlayer.use.lessonSubtitles(),
    duration: useLessonPlayer.use.duration(),
    skipIntroStartAt: useLessonPlayer.use.skipIntroStartAt(),
    skipIntroEndAt: useLessonPlayer.use.skipIntroEndAt(),
    staticBreakdowns: useLessonPlayer.use.staticBreakdowns(),
    quizBreakdowns: useLessonPlayer.use.quizBreakdowns(),
    feedbackType: useLessonPlayer.use.feedbackType(),
    currentVideoQuality: useLessonPlayer.use.currentVideoQuality(),
    currentVideoAudio: useLessonPlayer.use.currentVideoAudio(),
    currentVideoSpeed: useLessonPlayer.use.currentVideoSpeed(),
    currentSubtitle: useLessonPlayer.use.currentSubtitle(),
    lessonOrder: useLessonPlayer.use.lessonOrder(),
    isNextLessonCancelled: useLessonPlayer.use.isNextLessonCancelled(),
    isNextLessonVisible: useLessonPlayer.use.isNextLessonVisible(),
    isNextOverlayVisible: useLessonPlayer.use.isNextOverlayVisible(),
    setPlayer: useLessonPlayer.use.setPlayer(),
    setLessonPlaylist: useLessonPlayer.use.setLessonPlaylist(),
    setLessonSubtitles: useLessonPlayer.use.setLessonSubtitles(),
    setVideoSettings: useLessonPlayer.use.setVideoSettings(),
    setStaticBreakdowns: useLessonPlayer.use.setStaticBreakdowns(),
    setQuizBreakdowns: useLessonPlayer.use.setQuizBreakdowns(),
    setPaused: useLessonPlayer.use.setPaused(),
    setFeedback: useLessonPlayer.use.setFeedback(),
    setFullscreen: useLessonPlayer.use.setFullscreen(),
    setLessonDetails: useLessonPlayer.use.setLessonDetails(),
    setNextLesson: useLessonPlayer.use.setNextLesson(),
    setNextOverlayVisible: useLessonPlayer.use.setNextOverlayVisible(),
    setNextLessonVisible: useLessonPlayer.use.setNextLessonVisible(),
    setNextLessonCancelled: useLessonPlayer.use.setNextLessonCancelled(),
    setControlsIsVisible: useLessonPlayer.use.setControlsIsVisible(),
    setIsCoverLoaderVisible: useLessonPlayer.use.setIsCoverLoaderVisible(),
    setXtraIsVisible: useLessonPlayer.use.setXtraIsVisible(),
    setQuizIsVisible: useLessonPlayer.use.setQuizIsVisible(),
    setSkipIsVisible: useLessonPlayer.use.setSkipIsVisible(),
    resetLessonPlayer: useLessonPlayer.use.resetLessonPlayer()
  };
};

export { useLessonPlayerStore, useLessonPlayer };
